// Client API dei wizard configurabili.
//
// Due gruppi distinti, come lato server:
//   flowsRuntimeGet()  → GET flows          — solo pubblicati e attivi
//   il resto           → wizard-flows/*     — builder: bozze, versioni, rollback
//
// Stesse convenzioni degli altri client (`apiImpostazioni.ts`): istanza axios
// dedicata, token da localStorage, risposta in `{ success, data }`.

import axios, { type AxiosInstance } from 'axios'
import type { FlowDefinition } from '@/components/wizard/engine'

export interface FlowSummary {
  flow_key: string
  name: string
  description: string | null
  anchor: string
  icon: string | null
  color: string | null
  is_active: boolean
  sort_order: number
  published_version: number | null
  published_at: string | null
  draft_version: number | null
  has_unpublished_changes: boolean
  updated_by: string | null
  updated_at: string | null
}

export interface FlowVersionInfo {
  version: number
  status: 'draft' | 'published' | 'archived'
  notes: string | null
  created_by: string | null
  created_at: string | null
  published_at: string | null
}

export interface FlowDetail {
  flow: FlowSummary
  /** Grafo su cui si lavora: la bozza, o la pubblicata se non c'è bozza. */
  draft: FlowDefinition | null
  published: FlowDefinition | null
  versions: FlowVersionInfo[]
}

/** Errore con i messaggi di validazione del grafo restituiti dal server. */
export class FlowApiError extends Error {
  constructor(message: string, readonly issues: string[] = []) {
    super(message)
    this.name = 'FlowApiError'
  }
}

class ApiFlowService {
  private api: AxiosInstance

  constructor() {
    const env = (import.meta as any).env
    const apiUrl = env.VITE_DEBUG === 'true'
      ? env.VITE_API_RASOTOT_URL_DEBUG
      : env.VITE_API_RASOTOT_URL

    this.api = axios.create({
      baseURL: apiUrl,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      timeout: 40000,
    })

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken')
      if (token && config.headers) config.headers.Authorization = `Bearer ${token}`
      return config
    })
  }

  /**
   * Nessuno Swal qui: gli errori vengono rilanciati come `FlowApiError` e li
   * gestisce chi chiama. Il runtime dei flow non deve sbattere un popup in
   * faccia a un operatore perché il builder ha un problema.
   */
  private fail(error: any, fallback: string): never {
    const data = error?.response?.data
    const issues: string[] = Array.isArray(data?.errors) ? data.errors : []
    throw new FlowApiError(data?.message ?? error?.message ?? fallback, issues)
  }

  // ─── Runtime ────────────────────────────────────────────────────────────────

  async flowsRuntimeGet(anchor?: string): Promise<FlowDefinition[]> {
    try {
      const res = await this.api.get('flows', { params: anchor ? { anchor } : undefined })
      return res.data?.data ?? []
    } catch (e) {
      this.fail(e, 'Caricamento flow non riuscito')
    }
  }

  // ─── Builder ────────────────────────────────────────────────────────────────

  async listGet(): Promise<FlowSummary[]> {
    try {
      return (await this.api.get('wizard-flows')).data?.data ?? []
    } catch (e) {
      this.fail(e, 'Caricamento elenco flow non riuscito')
    }
  }

  async detailGet(key: string): Promise<FlowDetail> {
    try {
      return (await this.api.get(`wizard-flows/${encodeURIComponent(key)}`)).data?.data
    } catch (e) {
      this.fail(e, 'Caricamento flow non riuscito')
    }
  }

  async create(payload: Partial<FlowSummary> & { graph?: unknown }): Promise<FlowSummary> {
    try {
      return (await this.api.post('wizard-flows', payload)).data?.data
    } catch (e) {
      this.fail(e, 'Creazione flow non riuscita')
    }
  }

  /** Salva identità e/o grafo sulla bozza (la crea se non esiste). */
  async saveDraft(key: string, payload: Record<string, unknown>): Promise<FlowSummary> {
    try {
      return (await this.api.put(`wizard-flows/${encodeURIComponent(key)}`, payload)).data?.data
    } catch (e) {
      this.fail(e, 'Salvataggio bozza non riuscito')
    }
  }

  async publish(key: string, notes?: string): Promise<FlowSummary> {
    try {
      const res = await this.api.post(`wizard-flows/${encodeURIComponent(key)}/publish`, { notes })
      return res.data?.data
    } catch (e) {
      this.fail(e, 'Pubblicazione non riuscita')
    }
  }

  async unpublish(key: string): Promise<FlowSummary> {
    try {
      return (await this.api.post(`wizard-flows/${encodeURIComponent(key)}/unpublish`)).data?.data
    } catch (e) {
      this.fail(e, 'Ritiro non riuscito')
    }
  }

  async rollback(key: string, version: number): Promise<FlowSummary> {
    try {
      const res = await this.api.post(`wizard-flows/${encodeURIComponent(key)}/rollback/${version}`)
      return res.data?.data
    } catch (e) {
      this.fail(e, 'Ripristino non riuscito')
    }
  }

  async toggle(key: string): Promise<FlowSummary> {
    try {
      return (await this.api.post(`wizard-flows/${encodeURIComponent(key)}/toggle`)).data?.data
    } catch (e) {
      this.fail(e, 'Cambio stato non riuscito')
    }
  }

  async duplicate(key: string): Promise<FlowSummary> {
    try {
      return (await this.api.post(`wizard-flows/${encodeURIComponent(key)}/duplicate`)).data?.data
    } catch (e) {
      this.fail(e, 'Duplicazione non riuscita')
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await this.api.delete(`wizard-flows/${encodeURIComponent(key)}`)
    } catch (e) {
      this.fail(e, 'Eliminazione non riuscita')
    }
  }

  async versionGet(key: string, version: number): Promise<FlowDefinition> {
    try {
      const res = await this.api.get(`wizard-flows/${encodeURIComponent(key)}/versions/${version}`)
      return res.data?.data
    } catch (e) {
      this.fail(e, 'Caricamento versione non riuscito')
    }
  }
}

export const apiFlow = new ApiFlowService()
